import { useState, useRef, useEffect, useCallback, CSSProperties } from "react";
import { createPortal } from "react-dom";
import classes from "./Tooltip.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

const Tooltip = ({
  children,
  content,
  direction,
  delay = 400,
  className,
  hideOnClick = false,
  withTip = false,
  classNameWrapper,
  addComponent = false,
  usePortal = false,
}: any) => {
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [adjustedDirection, setAdjustedDirection] = useState(direction || "top");
  const [portalStyle, setPortalStyle] = useState<CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslationHook();

  const updatePortalPosition = useCallback(() => {
    if (!usePortal || !wrapperRef.current || !tooltipRef.current) return;

    const trigger = wrapperRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    const padding = 12;
    const gap = 8;
    const tooltipWidth = tooltip.width || 560;
    const tooltipHeight = tooltip.height || 0;

    let nextDirection = direction || "bottom";
    let top = nextDirection === "top" ? trigger.top - tooltipHeight - gap : trigger.bottom + gap;
    let left = trigger.right - tooltipWidth;

    if (nextDirection === "bottom" && top + tooltipHeight > window.innerHeight - padding) {
      nextDirection = "top";
      top = trigger.top - tooltipHeight - gap;
    } else if (nextDirection === "top" && top < padding) {
      nextDirection = "bottom";
      top = trigger.bottom + gap;
    }

    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));

    setAdjustedDirection(nextDirection);
    setPortalStyle({
      position: "fixed",
      top,
      left,
      transform: "none",
      zIndex: 1200,
    });
  }, [direction, usePortal]);

  useEffect(() => {
    if (!active || !tooltipRef.current) return undefined;

    if (usePortal) {
      updatePortalPosition();
      const frame = requestAnimationFrame(updatePortalPosition);
      window.addEventListener("scroll", updatePortalPosition, true);
      window.addEventListener("resize", updatePortalPosition);
      return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener("scroll", updatePortalPosition, true);
        window.removeEventListener("resize", updatePortalPosition);
      };
    }

    const tooltip = tooltipRef.current;
    const { right, left, top, bottom } = tooltip.getBoundingClientRect();
    const { innerWidth: vw, innerHeight: vh } = window;
    const padding = 10;

    let newDirection = direction || "top";
    const overflow = {
      right: right > vw - padding,
      left: left < padding,
      top: top < 0,
      bottom: bottom > vh,
    };

    // Auto-adjust direction
    if (
      (newDirection === "top" && overflow.top) ||
      (newDirection === "bottom" && overflow.bottom)
    ) {
      newDirection = overflow.top ? "bottom" : "top";
    } else if (
      (newDirection === "left" && overflow.left) ||
      (newDirection === "right" && overflow.right)
    ) {
      newDirection = overflow.left ? "right" : "left";
    }

    // Adjust horizontal position for top/bottom tooltips
    const isVertical = newDirection === "top" || newDirection === "bottom";
    const hasHorizontalOverflow = overflow.right || overflow.left;

    if (isVertical && hasHorizontalOverflow) {
      const offset = overflow.right ? -(right - vw + padding) : padding - left;
      tooltip.style.transform = `translateX(calc(-50% + ${offset}px))`;
      tooltip.style.setProperty("--arrow-offset", `${-offset}px`);
    } else {
      tooltip.style.transform = "";
      tooltip.style.setProperty("--arrow-offset", "0px");
    }

    setAdjustedDirection(newDirection);
    return undefined;
  }, [active, direction, usePortal, updatePortalPosition]);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
  };

  const showTip = (e: any) => {
    e.stopPropagation();
    clearHideTimeout();
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    showTimeoutRef.current = setTimeout(() => {
      setActive(true);
    }, delay || 400);
  };

  const hideTip = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = undefined;
    }
    if (usePortal) {
      clearHideTimeout();
      hideTimeoutRef.current = setTimeout(() => {
        setActive(false);
      }, 120);
      return;
    }
    setActive(false);
  };

  const handlePortalMouseEnter = () => {
    clearHideTimeout();
    setActive(true);
  };

  const renderContent = () => {
    const tooltipClassName = formatClassNames(
      classes.TooltipTip,
      usePortal && classes.portalTip,
      addComponent && classes.withoutTip,
      !addComponent && withTip && classes.withTip,
      className,
      !usePortal && classes[adjustedDirection],
    );

    if (addComponent) {
      return (
        <div
          ref={tooltipRef}
          data-testid="property-map-tooltip-text"
          className={tooltipClassName}
          style={usePortal ? portalStyle : undefined}
          onMouseEnter={usePortal ? handlePortalMouseEnter : undefined}
          onMouseLeave={usePortal ? hideTip : undefined}
          role="presentation"
        >
          {content}
        </div>
      );
    }
    return (
      <div
        ref={tooltipRef}
        data-testid="property-map-tooltip-text"
        className={formatClassNames(
          classes.TooltipTip,
          withTip ? classes.withTip : "",
          className,
          classes[adjustedDirection],
        )}
        style={usePortal ? portalStyle : undefined}
        onMouseEnter={usePortal ? handlePortalMouseEnter : undefined}
        onMouseLeave={usePortal ? hideTip : undefined}
        role="presentation"
        dangerouslySetInnerHTML={{
          __html: t(content),
        }}
      />
    );
  };

  const tooltipNode = active ? renderContent() : null;

  return (
    <div
      ref={wrapperRef}
      className={formatClassNames(classes.TooltipWrapper, classNameWrapper)}
      onClick={(e) => (!hideOnClick ? showTip(e) : null)}
      onMouseEnter={(e) => showTip(e)}
      onMouseLeave={hideTip}
      role="none"
    >
      {children}
      {usePortal && typeof document !== "undefined"
        ? tooltipNode && createPortal(tooltipNode, document.body)
        : tooltipNode}
    </div>
  );
};

export default wrapperHOC(Tooltip, {
  componentName: "Tooltip-Global",
  showForChina: true,
});
